import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { FormatChartData } from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { EmptyState } from "../../../components/recharts/ReMixCharts/components/EmptyState";
import { ChartType } from "../../../models/chart_model";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../../Rajasthan/const";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";
import {
  getJvvnlDemand,
  getJvvnlDiscomSubstations,
  getJvvnlFeederMetrics,
  getJvvnlFeeders,
  getJvvnlGeneration,
  getJvvnlPlants,
  getJvvnlSubstations,
  getJvvnlWeatherByLocation,
  getJvvnlEntityMetrics,
  JvvnlEntityMetricsResponse,
  getJvvnlLoadSummary,
  getJvvnlDemandForecast,
  getJvvnlGenerationForecast,
  getJvvnlFeederForecastByDate,
  JvvnlLoadSummaryRow,
  getJvvnlAggregatedWeather,
  getJvvnlDiscomSubDemandMetrics,
  getJvvnlTranscoDemandMetrics,
  getJvvnlCircleDemandMetrics,
} from "../../../Rest_api/RestApi";
import { getMonthStr } from "../Utility/demandHelpers";
import {
  JvvnlDemandRow,
  JvvnlFeeder,
  JvvnlFeederMetricResult,
  JvvnlGenerationRow,
  JvvnlPlant,
  JvvnlPlantType,
  JvvnlSubstation,
  JvvnlDiscomSubstation,
} from "../../../models/jvvnl";
import { SearchBox } from "../../../components/Search";
import MetricChip from "../../../components/Metrics";
import { FilterTab } from "../../../components/Filters";
import { DataTabs } from "../../Rajasthan/Demand/Data";

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================
const NET_CONSUMPTION_CIRCLE = { value: "KOTPUTLI", label: "KOTPUTLI" };
const NET_CONSUMPTION_CIRCLE_ID = 1;
const NET_CONSUMPTION_ALLOWED_TRANSCO = [
  "BANSUR",
  "BEHROR",
  "KOTPUTLI",
  "MANOHARPUR",
  "NIMRANA",
];
const JVVNL_DEMAND_UNITS = ["KW", "KWh", "KVA", "KVAh"] as const;
const JVVNL_AGG_UNITS = ["MW", "MWh"] as const;
const JVVNL_GEN_UNITS = ["KW", "KWh", "MW", "MWh"] as const;

const FUNCTIONS = ["Comparative", "Cumulative"] as const;
type FunctionMode = (typeof FUNCTIONS)[number];

const VARIABLES = ["Actual", "Weather", "Forecast"] as const;

const GEN_UNITS = ["KW", "KWh", "MW", "MWh", "LU"] as const;

function unitDataKey(unit: string): keyof JvvnlGenerationRow {
  if (unit === "KW") return "generation_kw";
  if (unit === "KWh") return "generation_kwh";
  if (unit === "MWh") return "generation_mwh";
  if (unit === "LU") return "generation_lu";
  return "generation_mw";
}

const WEATHER_PARAMS = [
  {
    key: "temperature_2m",
    label: "Temperature (°C)",
    chartLabel: "🌡️ Temperature (°C)",
    unit: "°C",
  },
  {
    key: "relative_humidity_2m",
    label: "Humidity (%)",
    chartLabel: "💧 Humidity (%)",
    unit: "%",
  },
  {
    key: "apparent_temperature",
    label: "Apparent Temp (°C)",
    chartLabel: "🌡️ Apparent Temp (°C)",
    unit: "°C",
  },
  {
    key: "precipitation",
    label: "Precipitation (mm)",
    chartLabel: "🌧️ Precipitation (mm)",
    unit: "mm",
  },
  { key: "rain", label: "Rain (mm)", chartLabel: "🌧️ Rain (mm)", unit: "mm" },
  {
    key: "cloud_cover",
    label: "Cloud Cover (%)",
    chartLabel: "☁️ Cloud Cover (%)",
    unit: "%",
  },
  {
    key: "wind_speed_10m",
    label: "Wind 10m (km/h)",
    chartLabel: "💨 Wind 10m (km/h)",
    unit: "km/h",
  },
  {
    key: "sunshine_duration",
    label: "Sunshine Duration (s)",
    chartLabel: "☀️ Sunshine Duration (s)",
    unit: "s",
  },
  {
    key: "direct_radiation_instant",
    label: "Direct Radiation Instant (W/m²)",
    chartLabel: "🔆 Direct Radiation Instant (W/m²)",
    unit: "W/m²",
  },
];

const Intervals = [
  { label: "Time Slot", key: "time", values: ["block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];
type IntervalOption = (typeof Intervals)[number];
type IntervalKey = IntervalOption["key"];

const DEMAND_TAB_KEY = "Actual";
const LEGACY_DEMAND_TAB_KEY = "Feeder Demand";

const GROUP_BY_OPTIONS = [
  { label: "Circle", rowKey: "group_discom", fallback: "KOTPUTLI" },
  {
    label: "Transco Substation",
    rowKey: "group_substation",
    fallback: "Transco Substation",
  },
  {
    label: "Discom Substation",
    rowKey: "group_discom_substation",
    fallback: "Discom Substation",
  },
  { label: "Feeder", rowKey: "group_feeder", fallback: "Feeder" },
  { label: "Generation", rowKey: "group_generation", fallback: "Generation" },
  {
    label: "Net Consumption",
    rowKey: "group_net_consumption",
    fallback: "Net Consumption",
  },
] as const;

type GroupByOption = (typeof GROUP_BY_OPTIONS)[number];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

// When all options are selected and the user clicks one (react-select toggles it
// off), isolate to just that single option instead of deselecting it.
function isolateOnAllSelected<T extends { value: any }>(
  prev: T[],
  next: T[],
  allOptions: T[],
): T[] {
  const selectableCount = allOptions.length;
  if (
    selectableCount > 1 &&
    prev.length === selectableCount &&
    next.length === selectableCount - 1
  ) {
    const removedValue = prev.find(
      (p) => !next.some((n) => n.value === p.value),
    );
    if (removedValue) return [removedValue];
  }
  return next;
}

function pickFirstString(
  source: Record<string, unknown> | undefined,
  keys: string[],
): string | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function getChartGroupingKeys(interval: IntervalOption): string[] {
  if (interval.key === "time") return ["date", "block"];
  if (interval.key === "date") return ["date"];
  return [interval.values[0]];
}

function getIntervalXKey(
  row: { date?: string; block?: number; month?: string },
  intervalKey: IntervalKey,
): string {
  if (intervalKey === "time") return `${row.date ?? ""}_${row.block ?? ""}`;
  if (intervalKey === "date") return row.date ?? "";
  return row.month ?? "";
}

type EmbeddedNet = {
  level: "transco" | "discom_sub" | "plant" | "circle" | "feeder";
  id: number | string;
  label: string;
};

type NetTarget = {
  level: "circle" | "transco" | "discom_sub";
  id: number | string | undefined;
  name: string | undefined;
};

function resolveNetTarget(
  netDiscom: { value: number } | null,
  netTransco: { value: number } | null,
  netCircle: { value: string } | null,
): NetTarget {
  if (netDiscom)
    return { level: "discom_sub", id: netDiscom.value, name: undefined };
  if (netTransco)
    return { level: "transco", id: netTransco.value, name: undefined };
  return { level: "circle", id: undefined, name: netCircle?.value };
}

function resolveEmbeddedFeederId(embedded?: EmbeddedNet): number | null {
  if (embedded?.level !== "feeder") return null;
  const n = Number(embedded.id);
  return Number.isNaN(n) ? null : n;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function JvvnlFeederDemand({
  embedded,
  weatherParam,
  netVariable,
  variablesOverride,
  dateRangeOverride,
  forecastVariants,
  forecastDateOverride,
}: {
  embedded?: EmbeddedNet;
  weatherParam?: string | null;
  netVariable?:
    | "Demand"
    | "Generation"
    | "Net Load"
    | "Actual"
    | "Forecast"
    | "Weather"
    | null;
  variablesOverride?: string[] | null;
  dateRangeOverride?: { startDate: Date; endDate: Date; key?: string } | null;
  forecastVariants?: string[] | null;
  forecastDateOverride?: string | null;
} = {}) {
  // --- Redux State ---
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const effectiveTabKey = useMemo(() => {
    if (DefaultDates?.[DEMAND_TAB_KEY]) return DEMAND_TAB_KEY;
    if (DefaultDates?.[LEGACY_DEMAND_TAB_KEY]) return LEGACY_DEMAND_TAB_KEY;
    if (activeTab === LEGACY_DEMAND_TAB_KEY) return DEMAND_TAB_KEY;
    return activeTab;
  }, [DefaultDates, activeTab]);

  const currentRangeDate =
    dateRangeOverride ?? DefaultDates?.[effectiveTabKey]?.defaultRangeDate;

  // --- Local State ---
  const [selectedUnit, setSelectedUnit] = useState<string>(
    JVVNL_DEMAND_UNITS[0],
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByOption>(
    embedded
      ? embedded.level === "plant"
        ? (GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_generation") ??
          GROUP_BY_OPTIONS[0])
        : embedded.level === "feeder"
          ? (GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_feeder") ??
            GROUP_BY_OPTIONS[0])
          : (GROUP_BY_OPTIONS.find(
              (o) => o.rowKey === "group_net_consumption",
            ) ?? GROUP_BY_OPTIONS[0])
      : (GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_net_consumption") ??
          GROUP_BY_OPTIONS[0]),
  );
  const [selectedBlock, setSelectedBlock] = useState<{ value: number }[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedFunction, setSelectedFunction] =
    useState<FunctionMode>("Comparative");
  const [selectedVariables, setSelectedVariables] = useState<string[]>([
    "Actual",
  ]);
  const [selectedWeatherParams, setSelectedWeatherParams] = useState<string[]>([
    "temperature_2m",
  ]);

  useEffect(() => {
    if (!embedded) return;
    const targetRowKey =
      embedded.level === "plant"
        ? "group_generation"
        : embedded.level === "feeder"
          ? "group_feeder"
          : "group_net_consumption";
    if (selectedGroupBy.rowKey === targetRowKey) return;
    const target = GROUP_BY_OPTIONS.find((o) => o.rowKey === targetRowKey);
    if (target) setSelectedGroupBy(target);
  }, [embedded, selectedGroupBy.rowKey]);

  // External weather param override (used by embedded popup sidebar)
  useEffect(() => {
    if (weatherParam == null) return;
    if (weatherParam === "") {
      setSelectedVariables((prev) => prev.filter((v) => v !== "Weather"));
      return;
    }
    setSelectedWeatherParams([weatherParam]);
    setSelectedVariables((prev) =>
      prev.includes("Weather") ? prev : [...prev, "Weather"],
    );
  }, [weatherParam]);

  useEffect(() => {
    if (variablesOverride != null) {
      setSelectedVariables(variablesOverride);
    }
  }, [variablesOverride]);

  // Metadata State
  const [feeders, setFeeders] = useState<JvvnlFeeder[]>([]);
  const [feedersLoaded, setFeedersLoaded] = useState(false);
  const [substations, setSubstations] = useState<JvvnlSubstation[]>([]);
  const [substationsLoaded, setSubstationsLoaded] = useState(false);

  // Filter Options State
  const [selectedFeederOptions, setSelectedFeederOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [selectedSubstationOptions, setSelectedSubstationOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [selectedFeederSubstations, setSelectedFeederSubstations] = useState<
    { value: string; label: string }[]
  >([]);

  // Net Consumption group mode
  const [netCircle, setNetCircle] = useState<{
    value: string;
    label: string;
  } | null>(NET_CONSUMPTION_CIRCLE);
  const [netTransco, setNetTransco] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [netDiscom, setNetDiscom] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [netLoadSummary, setNetLoadSummary] = useState<JvvnlLoadSummaryRow[]>(
    [],
  );

  // Discom Substation group mode
  const [discomSubstations, setDiscomSubstations] = useState<
    JvvnlDiscomSubstation[]
  >([]);
  const [discomSubstationsLoaded, setDiscomSubstationsLoaded] = useState(false);
  const [selectedTranscoForDiscom, setSelectedTranscoForDiscom] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedDiscomSubstations, setSelectedDiscomSubstations] = useState<
    { value: number; label: string }[]
  >([]);

  // Data State
  const [rawData, setRawData] = useState<JvvnlDemandRow[]>([]);
  const [gssLoadData, setGssLoadData] = useState<
    (JvvnlLoadSummaryRow & { series_group: string })[]
  >([]);
  const [discomData, setDiscomData] = useState<JvvnlLoadSummaryRow[]>([]);
  const [discomSubLoadData, setDiscomSubLoadData] = useState<
    (JvvnlLoadSummaryRow & { series_group: string })[]
  >([]);
  const [weatherData, setWeatherData] = useState<any[]>([]);
  const [forecastRows, setForecastRows] = useState<any[]>([]);
  const [groupMetrics, setGroupMetrics] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [netMetrics, setNetMetrics] =
    useState<JvvnlEntityMetricsResponse | null>(null);
  const [feederMetrics, setFeederMetrics] =
    useState<JvvnlFeederMetricResult | null>(null);

  // Generation State
  const [generationData, setGenerationData] = useState<JvvnlGenerationRow[]>(
    [],
  );
  const [allPlants, setAllPlants] = useState<JvvnlPlant[]>([]);
  const [plantsLoaded, setPlantsLoaded] = useState(false);
  const [selectedPlantTypeOptions, setSelectedPlantTypeOptions] = useState<
    { value: JvvnlPlantType; label: string }[]
  >([]);
  const [selectedPlantOptions, setSelectedPlantOptions] = useState<
    { value: number; label: string }[]
  >([]);

  // Chart State
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const weatherCacheRef = useRef(new Map<string, any[]>());
  const selectedBlockSet = useMemo(
    () =>
      selectedBlock.length > 0
        ? new Set(selectedBlock.map((blockOption) => blockOption.value))
        : null,
    [selectedBlock],
  );
  const formattedRange = useMemo(() => {
    if (!currentRangeDate) return null;
    return {
      startDate: formatDateForAPI(currentRangeDate.startDate),
      endDate: formatDateForAPI(currentRangeDate.endDate),
    };
  }, [currentRangeDate]);

  // ============================================================================
  // 1. METADATA LOADING (Substations & Feeders)
  // ============================================================================
  useEffect(() => {
    if (
      (selectedGroupBy.rowKey !== "group_substation" &&
        selectedGroupBy.rowKey !== "group_feeder" &&
        selectedGroupBy.rowKey !== "group_discom_substation") ||
      substationsLoaded ||
      substations.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlSubstations()
      .then((list) => {
        if (!cancelled) {
          const filteredList =
            selectedGroupBy.rowKey === "group_substation"
              ? list.filter((s) =>
                  [
                    "BANSUR",
                    "BEHROR",
                    "KOTPUTLI",
                    "NIMRANA",
                    "MANOHARPUR",
                  ].includes(s.station),
                )
              : list;
          setSubstations(filteredList);
        }
      })
      .catch(() => {
        if (!cancelled) setSubstations([]);
      })
      .finally(() => {
        if (!cancelled) setSubstationsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroupBy.rowKey, substations.length, substationsLoaded]);

  useEffect(() => {
    if (
      selectedGroupBy.rowKey !== "group_feeder" ||
      feedersLoaded ||
      feeders.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlFeeders()
      .then((list) => {
        if (!cancelled) setFeeders(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL feeders", e);
        if (!cancelled) setFeeders([]);
      })
      .finally(() => {
        if (!cancelled) setFeedersLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [feeders.length, feedersLoaded, selectedGroupBy.rowKey]);

  // Discom Substations loading
  useEffect(() => {
    if (
      (selectedGroupBy.rowKey !== "group_discom_substation" &&
        selectedGroupBy.rowKey !== "group_net_consumption" &&
        selectedGroupBy.rowKey !== "group_feeder") ||
      discomSubstationsLoaded ||
      discomSubstations.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlDiscomSubstations()
      .then((list) => {
        if (!cancelled) setDiscomSubstations(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL discom substations", e);
        if (!cancelled) setDiscomSubstations([]);
      })
      .finally(() => {
        if (!cancelled) setDiscomSubstationsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedGroupBy.rowKey,
    discomSubstationsLoaded,
    discomSubstations.length,
  ]);

  // Plants loading
  useEffect(() => {
    if (
      selectedGroupBy.rowKey !== "group_generation" &&
      selectedGroupBy.rowKey !== "group_net_consumption"
    )
      return;
    if (plantsLoaded) return;
    let cancelled = false;
    getJvvnlPlants()
      .then((list) => {
        if (!cancelled) setAllPlants(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL plants", e);
        if (!cancelled) setAllPlants([]);
      })
      .finally(() => {
        if (!cancelled) setPlantsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroupBy.rowKey, plantsLoaded]);

  const plantLocation = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of allPlants) m.set(p.id, p.location);
    return m;
  }, [allPlants]);

  const selectedPlantTypes = useMemo(
    () => selectedPlantTypeOptions.map((o) => o.value),
    [selectedPlantTypeOptions],
  );

  const plantOptions = useMemo(() => {
    const filtered =
      selectedPlantTypes.length === 0
        ? allPlants
        : allPlants.filter((p) => selectedPlantTypes.includes(p.plant_type));
    return filtered.map((p) => ({ value: p.id, label: p.name }));
  }, [allPlants, selectedPlantTypes]);

  const plantTypeOptions = useMemo(
    () =>
      Array.from(new Set(allPlants.map((p) => p.plant_type))).map((t) => ({
        value: t,
        label: t,
      })),
    [allPlants],
  );

  useEffect(() => {
    setSelectedPlantOptions((prev) =>
      prev.filter((o) => plantOptions.some((p) => p.value === o.value)),
    );
  }, [plantOptions]);

  const didInitPlantDefaultsRef = useRef(false);
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_generation") {
      didInitPlantDefaultsRef.current = false;
      return;
    }
    if (didInitPlantDefaultsRef.current) return;
    if (allPlants.length === 0) return;
    const firstType = allPlants[0].plant_type;
    const plantsOfType = allPlants.filter((p) => p.plant_type === firstType);
    setSelectedPlantTypeOptions([{ value: firstType, label: firstType }]);
    setSelectedPlantOptions(
      plantsOfType.map((p) => ({ value: p.id, label: p.name })),
    );
    didInitPlantDefaultsRef.current = true;
  }, [selectedGroupBy.rowKey, allPlants]);

  const selectedPlantIds = useMemo(
    () => selectedPlantOptions.map((o) => o.value),
    [selectedPlantOptions],
  );

  // Generation data fetch
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_generation") {
      setGenerationData([]);
      return;
    }
    if (!formattedRange) return;
    if (!plantsLoaded) return;
    if (allPlants.length === 0) {
      setGenerationData([]);
      setLoading(false);
      return;
    }
    if (selectedPlantIds.length === 0) {
      setGenerationData([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getJvvnlGeneration({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      plantIds: selectedPlantIds,
      plantLocation,
    })
      .then(({ rows }) => {
        if (!cancelled) setGenerationData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL generation:", err);
        if (!cancelled) setGenerationData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    plantsLoaded,
    allPlants.length,
    plantLocation,
    selectedGroupBy.rowKey,
    selectedPlantIds,
  ]);

  // ============================================================================
  // 2. MEMOS & SELECTIONS
  // ============================================================================
  const feederLocation = useMemo(
    () => new Map(feeders.map((f) => [f.id, f.location])),
    [feeders],
  );
  const substationNameByFeederId = useMemo(
    () =>
      new Map(
        feeders.map((f) => [
          f.id,
          pickFirstString(f as any, [
            "substation_name",
            "substation",
            "location_name",
          ]) || "Unknown Substation",
        ]),
      ),
    [feeders],
  );
  const substationById = useMemo(
    () => new Map(substations.map((s) => [s.id, s])),
    [substations],
  );
  const feederOptions = useMemo(
    () => feeders.map((f) => ({ value: f.id, label: f.name })),
    [feeders],
  );
  const substationOptions = useMemo(
    () => substations.map((s) => ({ value: s.id, label: s.station })),
    [substations],
  );

  // Unique substation names from feeders (for feeder-mode substation filter)
  const feederSubstationOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    for (const f of feeders) {
      const name = f.discom_substation_name;
      if (name && !seen.has(name)) {
        seen.add(name);
        out.push({ value: name, label: name });
      }
    }
    return out;
  }, [feeders]);

  // ── Net Consumption: derived option lists ──
  const netTranscoOptions = useMemo(() => {
    const seen = new Set<number>();
    const out: { value: number; label: string }[] = [];
    for (const d of discomSubstations) {
      if (d.transco_substation_id == null) continue;
      if (seen.has(d.transco_substation_id)) continue;
      const upper = (d.transco_substation_name || "").toUpperCase();
      if (!NET_CONSUMPTION_ALLOWED_TRANSCO.includes(upper)) continue;
      seen.add(d.transco_substation_id);
      out.push({
        value: d.transco_substation_id,
        label:
          d.transco_substation_name || `Transco ${d.transco_substation_id}`,
      });
    }
    return out;
  }, [discomSubstations]);

  const netDiscomOptions = useMemo(() => {
    const allowedTranscoIds = new Set(netTranscoOptions.map((t) => t.value));
    const filtered = netTransco
      ? discomSubstations.filter(
          (d) => d.transco_substation_id === netTransco.value,
        )
      : discomSubstations.filter(
          (d) =>
            d.transco_substation_id != null &&
            allowedTranscoIds.has(d.transco_substation_id),
        );
    return filtered.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [discomSubstations, netTransco, netTranscoOptions]);

  const netSeriesLabel = useMemo(() => {
    if (netDiscom) return `Discom Substation - ${netDiscom.label}`;
    if (netTransco) return `Transco - ${netTransco.label}`;
    return `Circle - ${netCircle?.label ?? "KOTPUTLI"}`;
  }, [netCircle, netTransco, netDiscom]);

  // Transco substations list available for Discom-Substation group filter
  const transcoOptionsForDiscom = useMemo(() => {
    const seen = new Set<number>();
    const out: { value: number; label: string }[] = [];
    for (const d of discomSubstations) {
      if (d.transco_substation_id == null) continue;
      if (seen.has(d.transco_substation_id)) continue;
      seen.add(d.transco_substation_id);
      out.push({
        value: d.transco_substation_id,
        label:
          d.transco_substation_name || `Transco ${d.transco_substation_id}`,
      });
    }
    return out;
  }, [discomSubstations]);

  // Discom substations filtered by selected transco (empty = all)
  const filteredDiscomSubstationOptions = useMemo(() => {
    const list =
      selectedTranscoForDiscom.length === 0
        ? discomSubstations
        : discomSubstations.filter((d) =>
            selectedTranscoForDiscom.some(
              (t) => t.value === d.transco_substation_id,
            ),
          );
    return list.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [discomSubstations, selectedTranscoForDiscom]);

  // Default: first transco + first discom substation under it, ONCE on initial load
  const didInitDiscomSubDefaultsRef = useRef(false);
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    if (didInitDiscomSubDefaultsRef.current) return;
    if (discomSubstations.length === 0) return;
    const firstTransco = transcoOptionsForDiscom[0];
    if (!firstTransco) return;
    setSelectedTranscoForDiscom([firstTransco]);
    const firstDiscom = discomSubstations
      .filter((d) => d.transco_substation_id === firstTransco.value)
      .map((d) => ({
        value: d.discom_substation_id,
        label: d.discom_substation_name,
      }))[0];
    setSelectedDiscomSubstations(firstDiscom ? [firstDiscom] : []);
    didInitDiscomSubDefaultsRef.current = true;
  }, [selectedGroupBy.rowKey, discomSubstations, transcoOptionsForDiscom]);

  // Keep discom substation selection valid relative to transco filter
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    if (selectedTranscoForDiscom.length === 0) return;
    const allowed = new Set(
      filteredDiscomSubstationOptions.map((o) => o.value),
    );
    setSelectedDiscomSubstations((prev) => {
      const valid = prev.filter((o) => allowed.has(o.value));
      return valid.length > 0
        ? valid
        : filteredDiscomSubstationOptions.slice(0, 1);
    });
  }, [
    selectedGroupBy.rowKey,
    selectedTranscoForDiscom,
    filteredDiscomSubstationOptions,
  ]);

  // Feeders filtered by selected feeder-mode substations (empty = show all)
  const filteredFeederOptions = useMemo(() => {
    if (selectedFeederSubstations.length === 0) return feederOptions;
    const selected = new Set(selectedFeederSubstations.map((s) => s.value));
    return feeders
      .filter((f) => selected.has(f.discom_substation_name))
      .map((f) => ({ value: f.id, label: f.name }));
  }, [feeders, feederOptions, selectedFeederSubstations]);

  const didInitFeederDefaultsRef = useRef(false);

  // Default: pick first substation + its feeders, ONCE on initial load
  // Skip in embedded feeder mode — seeding is handled by embeddedSeededRef effect.
  useEffect(() => {
    if (embedded?.level === "feeder") return;
    if (didInitFeederDefaultsRef.current) return;
    if (feeders.length === 0) return;
    const firstSub = feederSubstationOptions[0];
    if (!firstSub) return;
    const subDefaults = [firstSub];
    setSelectedFeederSubstations(subDefaults);
    const defaultFeeders = feeders
      .filter((f) => f.discom_substation_name === firstSub.value)
      .map((f) => ({ value: f.id, label: f.name }));
    setSelectedFeederOptions(defaultFeeders);
    didInitFeederDefaultsRef.current = true;
  }, [feeders, feederSubstationOptions, embedded?.level]);

  // Keep feeder selection aligned with selected feeder-substation(s).
  // If current feeder selection becomes invalid for chosen substations,
  // default to all feeders inside the selected substation(s).
  // Skip while embedded seeding hasn't completed yet to avoid overwriting it.
  useEffect(() => {
    if (embedded?.level === "feeder" && !embeddedSeededRef.current) return;
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (selectedFeederSubstations.length === 0) return;

    const selectedSubNames = new Set(
      selectedFeederSubstations.map((s) => s.value),
    );
    const feedersInSelectedSubstations = feeders
      .filter((f) => selectedSubNames.has(f.discom_substation_name))
      .map((f) => ({ value: f.id, label: f.name }));

    setSelectedFeederOptions((prev) => {
      const allowedIds = new Set(
        feedersInSelectedSubstations.map((f) => f.value),
      );
      const validPrev = prev.filter((f) => allowedIds.has(f.value));
      return validPrev.length > 0 ? validPrev : feedersInSelectedSubstations;
    });
  }, [
    feeders,
    selectedGroupBy.rowKey,
    selectedFeederSubstations,
    embedded?.level,
  ]);

  useEffect(() => {
    setSelectedSubstationOptions((prev) => {
      const filtered = prev.filter((o) =>
        substationOptions.some((s) => s.value === o.value),
      );
      return filtered.length === 0 && substationOptions.length > 0
        ? [substationOptions[0]]
        : filtered;
    });
  }, [substationOptions]);

  const generationOnlyMode = selectedGroupBy.rowKey === "group_generation";

  // Adjust units based on group by / variable mode
  useEffect(() => {
    if (generationOnlyMode) {
      if (!JVVNL_GEN_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_GEN_UNITS[0]);
      return;
    }
    if (selectedGroupBy.rowKey === "group_net_consumption") {
      if (selectedUnit !== "MW") setSelectedUnit("MW");
      return;
    }
    if (
      selectedGroupBy.rowKey === "group_feeder" ||
      selectedGroupBy.rowKey === "group_discom_substation"
    ) {
      if (!JVVNL_DEMAND_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_DEMAND_UNITS[0]);
    } else {
      if (!JVVNL_AGG_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_AGG_UNITS[0]);
    }
  }, [selectedGroupBy.rowKey, selectedUnit, generationOnlyMode]);

  // Extract selected IDs
  const selectedFeederIds = useMemo(
    () => selectedFeederOptions.map((o) => o.value),
    [selectedFeederOptions],
  );
  const selectedSubstationIds = useMemo(
    () => selectedSubstationOptions.map((o) => o.value),
    [selectedSubstationOptions],
  );
  const selectedSubstationNames = useMemo(
    () =>
      selectedSubstationIds
        .map((id) => substationById.get(id)?.station)
        .filter(Boolean) as string[],
    [selectedSubstationIds, substationById],
  );

  const selectedDiscomSubstationIds = useMemo(
    () => selectedDiscomSubstations.map((o) => o.value),
    [selectedDiscomSubstations],
  );

  const effectiveFeederIds = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_feeder") return selectedFeederIds;
    if (selectedGroupBy.rowKey === "group_substation") {
      if (selectedSubstationNames.length === 0) return [];
      return feeders
        .filter((f) =>
          selectedSubstationNames.includes(
            substationNameByFeederId.get(f.id) || "Unknown Substation",
          ),
        )
        .map((f) => f.id);
    }
    return [];
  }, [
    feeders,
    selectedFeederIds,
    selectedGroupBy.rowKey,
    selectedSubstationNames,
    substationNameByFeederId,
  ]);

  const selectedWeatherParam =
    selectedWeatherParams[0] || WEATHER_PARAMS[0].key;
  const selectedWeatherParamMeta = useMemo(
    () =>
      WEATHER_PARAMS.find((w) => w.key === selectedWeatherParam) ||
      WEATHER_PARAMS[0],
    [selectedWeatherParam],
  );
  const selectedWeatherMetricLabel =
    selectedWeatherParamMeta.chartLabel ||
    selectedWeatherParamMeta.label ||
    selectedWeatherParam;
  const chartGroupingKeys = useMemo(
    () => getChartGroupingKeys(selectedInterval),
    [selectedInterval],
  );
  const getXKeyForInterval = useCallback(
    (row: { date?: string; block?: number; month?: string }) =>
      getIntervalXKey(row, selectedInterval.key as IntervalKey),
    [selectedInterval.key],
  );

  const weatherSeries = useMemo(() => {
    if (!selectedVariables.includes("Weather")) return [];
    if (selectedGroupBy.rowKey === "group_feeder") return selectedFeederOptions;
    if (selectedGroupBy.rowKey === "group_substation")
      return selectedSubstationOptions;
    if (selectedGroupBy.rowKey === "group_discom_substation")
      return selectedDiscomSubstations;
    if (selectedGroupBy.rowKey === "group_net_consumption") {
      return [{ value: "net", label: netSeriesLabel }];
    }
    if (
      selectedGroupBy.rowKey === "group_generation" &&
      selectedPlantOptions.length > 0
    ) {
      return selectedPlantOptions.map((p) => ({
        value: `plant_${p.value}`,
        label: p.label,
      }));
    }
    return [{ value: 1, label: "KOTPUTLI" }];
  }, [
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederOptions,
    selectedSubstationOptions,
    selectedPlantOptions,
    selectedDiscomSubstations,
    netSeriesLabel,
  ]);

  const forecastSeries = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_net_consumption") {
      const baseLabel =
        selectedFunction === "Cumulative" ? "Total" : netSeriesLabel;
      const allVariants = ["Demand", "Generation", "Net Load"];
      const variants =
        forecastVariants && forecastVariants.length > 0
          ? allVariants.filter((v) => forecastVariants.includes(v))
          : netVariable
            ? [netVariable]
            : allVariants;
      return variants.map((v) => {
        const tag =
          v === "Net Load"
            ? "Net"
            : v === "Generation"
              ? "Generation"
              : "Demand";
        return {
          value: `${baseLabel} (${tag})` as any,
          label: `${baseLabel} ${tag}`,
        };
      });
    }
    if (selectedGroupBy.rowKey === "group_feeder")
      return selectedFeederOptions.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    if (selectedGroupBy.rowKey === "group_discom")
      return [{ value: "KOTPUTLI" as any, label: "KOTPUTLI" }];
    if (selectedGroupBy.rowKey === "group_substation")
      return selectedSubstationOptions.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    if (selectedGroupBy.rowKey === "group_discom_substation")
      return selectedDiscomSubstations.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    if (selectedGroupBy.rowKey === "group_generation")
      return selectedPlantOptions.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    return selectedSubstationOptions;
  }, [
    selectedGroupBy.rowKey,
    selectedSubstationOptions,
    selectedDiscomSubstations,
    selectedFeederOptions,
    selectedPlantOptions,
    selectedFunction,
    netSeriesLabel,
    netVariable,
    forecastVariants,
  ]);

  // ============================================================================
  // 3. DATA FETCHING (Demand, GSS Load, Discom Energy)
  // ============================================================================
  // Actual Data
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!formattedRange) {
      setLoading(false);
      return;
    }
    // After embedded seeding completes, selectedFeederIds is the source of truth.
    // Only fall back to embedded.id before seeding fires (initial load).
    const feederIdsForFetch =
      selectedFeederIds.length > 0
        ? selectedFeederIds
        : !embeddedSeededRef.current
          ? (() => {
              const eid = resolveEmbeddedFeederId(embedded);
              return eid != null ? [eid] : [];
            })()
          : [];
    if (feederIdsForFetch.length === 0) {
      setRawData([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getJvvnlDemand({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      feederIds: feederIdsForFetch,
      feederLocation,
    })
      .then(({ rows }) => {
        if (!cancelled) setRawData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL Actual:", err);
        if (!cancelled) setRawData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    feederLocation,
    selectedFeederIds,
    selectedGroupBy.rowKey,
    embedded?.id,
    embedded?.level,
    embedded,
  ]);

  // Metrics: net mode → /entity-metrics/; feeder mode → /jvvnl-metrics/feeder/;
  // group (circle/transco/discom_sub) → /jvvnl-metrics/*
  useEffect(() => {
    if (!formattedRange) return;
    let cancelled = false;
    const isNet = selectedGroupBy.rowKey === "group_net_consumption";
    const isFeeder = selectedGroupBy.rowKey === "group_feeder";
    const isGroupDiscomSub =
      selectedGroupBy.rowKey === "group_discom_substation";
    const isGroupTransco = selectedGroupBy.rowKey === "group_substation";
    const isGroupCircle = selectedGroupBy.rowKey === "group_discom";
    if (isNet) {
      if (!cancelled) {
        setGroupMetrics(null);
        setFeederMetrics(null);
      }
      const { level, id, name } = resolveNetTarget(
        netDiscom,
        netTransco,
        netCircle,
      );
      getJvvnlEntityMetrics({
        startDate: formattedRange.startDate,
        endDate: formattedRange.endDate,
        level,
        id,
        name,
      })
        .then((res) => {
          if (!cancelled) setNetMetrics(res);
        })
        .catch((err) => {
          console.error("Error fetching JVVNL entity metrics:", err);
          if (!cancelled) setNetMetrics(null);
        });
    } else if (isFeeder) {
      if (!cancelled) {
        setNetMetrics(null);
        setGroupMetrics(null);
      }
      const feederIds =
        selectedFeederIds.length > 0
          ? selectedFeederIds
          : !embeddedSeededRef.current
            ? (() => {
                const eid = resolveEmbeddedFeederId(embedded);
                return eid != null ? [eid] : [];
              })()
            : [];
      if (feederIds.length === 0) {
        if (!cancelled) setFeederMetrics(null);
        return;
      }
      getJvvnlFeederMetrics({
        startDate: formattedRange.startDate,
        endDate: formattedRange.endDate,
        level: "feeder",
        ids: feederIds,
        combine: true,
      })
        .then((res) => {
          if (!cancelled) setFeederMetrics(res?.results?.[0] ?? null);
        })
        .catch((err) => {
          console.error("Error fetching JVVNL feeder metrics:", err);
          if (!cancelled) setFeederMetrics(null);
        });
    } else if (isGroupDiscomSub || isGroupTransco || isGroupCircle) {
      if (!cancelled) {
        setNetMetrics(null);
        setFeederMetrics(null);
        setGroupMetrics(null);
      }
      const ids = isGroupDiscomSub
        ? selectedDiscomSubstationIds
        : isGroupTransco
          ? selectedSubstationIds
          : [NET_CONSUMPTION_CIRCLE_ID];
      if (!ids || ids.length === 0) {
        if (!cancelled) setGroupMetrics(null);
        return;
      }
      const fetcher = isGroupCircle
        ? getJvvnlCircleDemandMetrics({
            startDate: formattedRange.startDate,
            endDate: formattedRange.endDate,
            level: "circle",
            ids,
            combine: true,
          })
        : isGroupTransco
          ? getJvvnlTranscoDemandMetrics({
              startDate: formattedRange.startDate,
              endDate: formattedRange.endDate,
              level: "transco",
              ids,
              combine: true,
            })
          : getJvvnlDiscomSubDemandMetrics({
              startDate: formattedRange.startDate,
              endDate: formattedRange.endDate,
              level: "discom_sub",
              ids,
              combine: true,
            });
      fetcher
        .then((res) => {
          if (!cancelled) setGroupMetrics(res?.results?.[0] ?? null);
        })
        .catch((err) => {
          console.error("Error fetching JVVNL group metrics:", err);
          if (!cancelled) setGroupMetrics(null);
        });
    } else {
      if (!cancelled) {
        setNetMetrics(null);
        setFeederMetrics(null);
        setGroupMetrics(null);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    netCircle,
    netTransco,
    netDiscom,
    selectedFeederIds,
    selectedSubstationIds,
    selectedDiscomSubstationIds,
    embedded?.id,
    embedded?.level,
    embedded,
  ]);

  // Discom Substation Load Data
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") {
      setDiscomSubLoadData([]);
      return;
    }
    if (!formattedRange) {
      setLoading(false);
      return;
    }
    if (selectedDiscomSubstationIds.length === 0) {
      setDiscomSubLoadData([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all(
      selectedDiscomSubstations.map((opt) =>
        getJvvnlLoadSummary({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "discom_sub",
          id: opt.value,
        }).then(({ rows }) =>
          rows.map((r) => ({ ...r, series_group: opt.label })),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled) setDiscomSubLoadData(results.flat());
      })
      .catch((err) => {
        console.error("Error fetching JVVNL discom substation load:", err);
        if (!cancelled) setDiscomSubLoadData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedDiscomSubstationIds,
    selectedDiscomSubstations,
  ]);

  // Net Consumption: aggregated weather via /weather/aggregated/
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") return;
    if (!formattedRange) return;
    if (!selectedVariables.includes("Weather")) {
      setWeatherData([]);
      return;
    }
    const { level, id, name } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
    );

    let cancelled = false;
    getJvvnlAggregatedWeather({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level,
      id,
      name,
    })
      .then(({ rows }) => {
        if (cancelled) return;
        // Weather is 15-min (1..96); demand/load-summary slot is 30-min (1..48).
        // Map 15-min time_block → 30-min slot: slot = ceil(time_block / 2)
        setWeatherData(
          rows.map((r) => ({
            ...r,
            seriesId: "net",
            seriesLabel: netSeriesLabel,
            block: Math.ceil(((r.time_block as number) || 0) / 2),
            time_block: r.time_block,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setWeatherData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    netCircle,
    netTransco,
    netDiscom,
    netSeriesLabel,
  ]);

  // Net Consumption: use /load-summary/ endpoint (demand + generation + net)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") {
      setNetLoadSummary([]);
      return;
    }
    if (!formattedRange) {
      setLoading(false);
      return;
    }
    const { level, id, name } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
    );

    let cancelled = false;
    setLoading(true);
    getJvvnlLoadSummary({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level,
      id,
      name,
    })
      .then(({ rows }) => {
        if (!cancelled) setNetLoadSummary(rows);
      })
      .catch(() => {
        if (!cancelled) setNetLoadSummary([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    netCircle,
    netTransco,
    netDiscom,
  ]);

  // Net Consumption: demand forecast
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") return;
    if (!selectedVariables.includes("Forecast") || !formattedRange) {
      setForecastRows([]);
      return;
    }
    const { level, id, name } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
    );
    const baseLabel =
      selectedFunction === "Cumulative" ? "Total" : netSeriesLabel;
    const variants: {
      tag: "Demand" | "Generation" | "Net";
      pick: (p: any) => number;
      varName: string;
    }[] = [
      { tag: "Demand", pick: (p) => p.demand_mw, varName: "Demand" },
      {
        tag: "Generation",
        pick: (p) => p.generation_mw,
        varName: "Generation",
      },
      { tag: "Net", pick: (p) => p.net_mw, varName: "Net Load" },
    ];
    const activeVariants = netVariable
      ? variants.filter((v) => v.varName === netVariable)
      : variants;

    let cancelled = false;
    const forecastArgs = forecastDateOverride
      ? { date: forecastDateOverride }
      : {
          start_date: formattedRange.startDate,
          end_date: formattedRange.endDate,
        };
    // Wrapper already fetches generation and sums multi-plant rows into demand_mw/generation_mw/net_mw.
    getJvvnlDemandForecast({ level, id, name, ...forecastArgs })
      .then((demandRes) => {
        if (cancelled) return;
        const fallbackDate =
          demandRes.forecast_date || formattedRange.startDate;
        const out: any[] = [];
        for (const dRow of demandRes.rows) {
          const merged = {
            demand_mw: dRow.demand_mw,
            generation_mw: dRow.generation_mw,
            net_mw: dRow.net_mw,
          };
          const d = dRow.date || fallbackDate;
          for (const v of activeVariants) {
            out.push({
              substationId: `${baseLabel} (${v.tag})`,
              substationName: `${baseLabel} ${v.tag}`,
              date: d,
              block: dRow.slot,
              month: getMonthStr(d),
              forecast_mw: v.pick(merged),
            });
          }
        }
        setForecastRows(out);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching JVVNL forecasts:", err);
        setForecastRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    netCircle,
    netTransco,
    netDiscom,
    selectedFunction,
    netSeriesLabel,
    netVariable,
    forecastDateOverride,
  ]);

  // Transco Substation Data (via /load-summary/ level=transco)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_substation") {
      setGssLoadData([]);
      return;
    }
    if (!formattedRange || selectedSubstationIds.length === 0) {
      setGssLoadData([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all(
      selectedSubstationOptions.map((opt) =>
        getJvvnlLoadSummary({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "transco",
          id: opt.value,
        }).then(({ rows }) =>
          rows.map((r) => ({ ...r, series_group: opt.label })),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled) setGssLoadData(results.flat());
      })
      .catch((err) => {
        console.error("Error fetching Transco load summary:", err);
        if (!cancelled) setGssLoadData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedSubstationIds,
    selectedSubstationOptions,
  ]);

  // Circle Data (via /load-summary/ level=circle)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom") {
      setDiscomData([]);
      return;
    }
    if (!formattedRange) {
      setDiscomData([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getJvvnlLoadSummary({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level: "circle",
      name: "KOTPUTLI",
    })
      .then(({ rows }) => {
        if (!cancelled) setDiscomData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL circle data:", err);
        if (!cancelled) setDiscomData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formattedRange, selectedGroupBy.rowKey]);

  // ============================================================================
  // 4. WEATHER & FORECAST FETCHING
  // ============================================================================
  // Weather — discom substation: one aggregated weather call per selected discom sub
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    if (!formattedRange || !selectedVariables.includes("Weather")) {
      setWeatherData([]);
      return;
    }
    if (selectedDiscomSubstations.length === 0) {
      setWeatherData([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      selectedDiscomSubstations.map((opt) =>
        getJvvnlAggregatedWeather({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "discom_sub",
          id: opt.value,
        }).then(({ rows }) =>
          rows.map((r) => ({
            ...r,
            seriesId: opt.value,
            seriesLabel: opt.label,
            block: Math.ceil(((r.time_block as number) || 0) / 2),
          })),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled) setWeatherData(results.flat());
      })
      .catch(() => {
        if (!cancelled) setWeatherData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedDiscomSubstations,
  ]);

  // Weather — feeder: aggregated weather by feeder's discom_substation_id
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!formattedRange || !selectedVariables.includes("Weather")) {
      setWeatherData([]);
      return;
    }
    if (selectedFeederOptions.length === 0 || feeders.length === 0) {
      setWeatherData([]);
      return;
    }

    // Group selected feeders by their discom_substation_id
    const feederById = new Map(feeders.map((f) => [f.id, f]));
    const subToFeeders = new Map<
      number,
      { subName: string; feeders: { id: number; name: string }[] }
    >();
    for (const opt of selectedFeederOptions) {
      const f = feederById.get(opt.value);
      if (!f || f.discom_substation_id == null) continue;
      const existing = subToFeeders.get(f.discom_substation_id) || {
        subName: f.discom_substation_name,
        feeders: [],
      };
      existing.feeders.push({ id: f.id, name: f.name });
      subToFeeders.set(f.discom_substation_id, existing);
    }
    if (subToFeeders.size === 0) {
      setWeatherData([]);
      return;
    }

    let cancelled = false;
    Promise.all(
      Array.from(subToFeeders.entries()).map(([subId, { feeders: fdrs }]) =>
        getJvvnlAggregatedWeather({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "discom_sub",
          id: subId,
        }).then(({ rows }) =>
          fdrs.flatMap((fdr) =>
            rows.map((r) => ({
              ...r,
              seriesId: fdr.id,
              seriesLabel: fdr.name,
              block: Math.ceil(((r.time_block as number) || 0) / 2),
            })),
          ),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled) setWeatherData(results.flat());
      })
      .catch((err) => {
        console.error("Error fetching feeder aggregated weather:", err);
        if (!cancelled) setWeatherData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederOptions,
    feeders,
  ]);

  // Weather — other modes (circle / transco / generation)
  useEffect(() => {
    if (selectedGroupBy.rowKey === "group_net_consumption") return;
    if (selectedGroupBy.rowKey === "group_discom_substation") return;
    if (selectedGroupBy.rowKey === "group_feeder") return;
    if (
      !["group_discom", "group_substation", "group_generation"].includes(
        selectedGroupBy.rowKey,
      ) ||
      !formattedRange ||
      !selectedVariables.includes("Weather")
    ) {
      setWeatherData([]);
      return;
    }

    let cancelled = false;

    const weatherTargets =
      selectedGroupBy.rowKey === "group_substation"
        ? selectedSubstationOptions.map((o) => ({
            seriesId: o.value,
            seriesLabel: o.label,
            locationId: substationById.get(o.value)?.location_id ?? o.value,
          }))
        : selectedGroupBy.rowKey === "group_generation" &&
            selectedPlantOptions.length > 0
          ? selectedPlantOptions
              .map((o) => {
                const loc = plantLocation.get(o.value);
                return loc == null
                  ? null
                  : {
                      seriesId: `plant_${o.value}` as any,
                      seriesLabel: o.label,
                      locationId: loc,
                    };
              })
              .filter(
                (
                  v,
                ): v is {
                  seriesId: any;
                  seriesLabel: string;
                  locationId: number;
                } => v !== null,
              )
          : [{ seriesId: 1, seriesLabel: "KOTPUTLI", locationId: 1 }];

    if (weatherTargets.length === 0) {
      setWeatherData([]);
      return;
    }

    (async () => {
      try {
        const uniqueLocations = new Map<
          number,
          { seriesId: number; seriesLabel: string }[]
        >();
        for (const target of weatherTargets) {
          const existing = uniqueLocations.get(target.locationId) || [];
          existing.push({
            seriesId: target.seriesId,
            seriesLabel: target.seriesLabel,
          });
          uniqueLocations.set(target.locationId, existing);
        }

        const allWeatherData = (
          await Promise.all(
            Array.from(uniqueLocations.entries()).map(
              async ([locationId, seriesTargets]) => {
                const cacheKey = `${locationId}|${formattedRange.startDate}|${formattedRange.endDate}`;
                let rows = weatherCacheRef.current.get(cacheKey);
                if (!rows) {
                  rows = await getJvvnlWeatherByLocation({
                    startDate: formattedRange.startDate,
                    endDate: formattedRange.endDate,
                    locationId,
                  });
                  weatherCacheRef.current.set(cacheKey, rows);
                }
                return seriesTargets.flatMap((target) =>
                  rows.map((row) => ({
                    ...row,
                    seriesId: target.seriesId,
                    seriesLabel: target.seriesLabel,
                    block: (row.time_block as number) || 0,
                  })),
                );
              },
            ),
          )
        ).flat();

        if (!cancelled) setWeatherData(allWeatherData);
      } catch (err) {
        console.error("Error fetching weather data:", err);
        if (!cancelled) setWeatherData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedSubstationOptions,
    substationById,
    selectedVariables,
    selectedPlantOptions,
    plantLocation,
  ]);

  // Forecast (circle / transco / discom_sub via /demand-forecast/)
  useEffect(() => {
    const rowKey = selectedGroupBy.rowKey;
    const isNetMode = rowKey === "group_net_consumption";
    const isFeederMode = rowKey === "group_feeder";
    if (isNetMode || isFeederMode) return; // handled by separate effects

    if (!selectedVariables.includes("Forecast") || !formattedRange) {
      setForecastRows([]);
      return;
    }

    const { startDate, endDate } = formattedRange;

    if (rowKey === "group_discom") {
      let cancelled = false;
      getJvvnlDemandForecast({
        level: "circle",
        name: "KOTPUTLI",
        start_date: startDate,
        end_date: endDate,
      })
        .then(({ rows, forecast_date }) => {
          if (cancelled) return;
          const d = forecast_date || startDate;
          setForecastRows(
            rows.map((p) => ({
              substationId: "KOTPUTLI",
              substationName: "KOTPUTLI",
              date: p.date || d,
              block: p.block,
              month: getMonthStr(p.date || d),
              forecast_mw: p.demand_mw,
            })),
          );
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching circle forecast:", err);
            setForecastRows([]);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (rowKey === "group_substation") {
      if (selectedSubstationOptions.length === 0) {
        setForecastRows([]);
        return;
      }
      let cancelled = false;
      Promise.all(
        selectedSubstationOptions.map((opt) =>
          getJvvnlDemandForecast({
            level: "transco",
            id: opt.value,
            start_date: startDate,
            end_date: endDate,
          }).then(({ rows, forecast_date }) => {
            const d = forecast_date || startDate;
            return rows.map((p) => ({
              substationId: opt.value,
              substationName: opt.label,
              date: p.date || d,
              block: p.block,
              month: getMonthStr(p.date || d),
              forecast_mw: p.demand_mw,
            }));
          }),
        ),
      )
        .then((results) => {
          if (!cancelled) setForecastRows(results.flat());
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching transco forecast:", err);
            setForecastRows([]);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (rowKey === "group_discom_substation") {
      if (selectedDiscomSubstations.length === 0) {
        setForecastRows([]);
        return;
      }
      let cancelled = false;
      Promise.all(
        selectedDiscomSubstations.map((opt) =>
          getJvvnlDemandForecast({
            level: "discom_sub",
            id: opt.value,
            start_date: startDate,
            end_date: endDate,
          }).then(({ rows, forecast_date }) => {
            const d = forecast_date || startDate;
            return rows.map((p) => ({
              substationId: opt.value,
              substationName: opt.label,
              date: p.date || d,
              block: p.block,
              month: getMonthStr(p.date || d),
              forecast_mw: p.demand_mw,
            }));
          }),
        ),
      )
        .then((results) => {
          if (!cancelled) setForecastRows(results.flat());
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching discom sub forecast:", err);
            setForecastRows([]);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (rowKey === "group_generation") {
      if (selectedPlantOptions.length === 0) {
        setForecastRows([]);
        return;
      }
      let cancelled = false;
      Promise.all(
        selectedPlantOptions.map((plant) =>
          getJvvnlGenerationForecast({
            level: "plant",
            id: plant.value,
            start_date: startDate,
            end_date: endDate,
          })
            .then(({ rows, forecast_date }) => {
              const d = forecast_date || startDate;
              // level=plant returns one row per slot; if API still returns
              // multi-row, sum per slot to be safe.
              const sumBySlot = new Map<
                string,
                { date: string; slot: number; gen: number }
              >();
              for (const p of rows) {
                const date = p.date || d;
                const key = `${date}|${p.slot}`;
                const cur = sumBySlot.get(key);
                if (cur) cur.gen += p.generation_mw;
                else
                  sumBySlot.set(key, {
                    date,
                    slot: p.slot,
                    gen: p.generation_mw,
                  });
              }
              return Array.from(sumBySlot.values()).map((p) => ({
                substationId: plant.value,
                substationName: plant.label,
                date: p.date,
                block: p.slot,
                month: getMonthStr(p.date),
                forecast_mw: p.gen,
              }));
            })
            .catch((err) => {
              console.error("Error fetching generation forecast:", err);
              return [] as any[];
            }),
        ),
      )
        .then((results) => {
          if (!cancelled) setForecastRows(results.flat());
        })
        .catch(() => {
          if (!cancelled) setForecastRows([]);
        });
      return () => {
        cancelled = true;
      };
    }

    setForecastRows([]);
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedSubstationOptions,
    selectedDiscomSubstations,
    selectedPlantOptions,
    plantLocation,
    selectedVariables,
  ]);

  // Feeder forecast (via /jvvnl/api/feeder-demand-forecast/)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!selectedVariables.includes("Forecast") || !formattedRange) {
      setForecastRows([]);
      return;
    }
    const feederIdsForFetch =
      selectedFeederIds.length > 0
        ? selectedFeederIds
        : !embeddedSeededRef.current
          ? (() => {
              const eid = resolveEmbeddedFeederId(embedded);
              return eid != null ? [eid] : [];
            })()
          : [];
    if (feederIdsForFetch.length === 0) {
      setForecastRows([]);
      return;
    }
    let cancelled = false;
    getJvvnlFeederForecastByDate({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      feederIds: feederIdsForFetch,
    })
      .then(({ data }) => {
        if (cancelled) return;
        setForecastRows(
          data.map((r) => ({
            substationId: r.feeder_id,
            substationName: r.feeder_name,
            date: r.date,
            block: r.slot,
            month: getMonthStr(r.date),
            forecast_mw: r.predicted_kw / 1000,
          })),
        );
      })
      .catch((err) => {
        console.error("Error fetching JVVNL feeder forecast:", err);
        if (!cancelled) setForecastRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederIds,
    embedded?.id,
    embedded?.level,
    embedded,
  ]);

  // ============================================================================
  // 5. CHART DATA FORMATTING & LEGENDS
  // ============================================================================
  const activeDataKey = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_net_consumption") return "net_mw";
    const unitToKey: Record<string, string> = {
      KW: "demand_kw",
      KWh: "demand_kwh",
      KVA: "demand_kva",
      KVAh: "demand_kvah",
      MW: "demand_mw",
      MWh: "demand_mwh",
    };
    const aggOnly = (u: string) => (u === "MWh" ? "demand_mwh" : "demand_mw");
    if (
      selectedGroupBy.rowKey === "group_discom" ||
      selectedGroupBy.rowKey === "group_substation"
    )
      return aggOnly(selectedUnit);
    if (selectedGroupBy.rowKey === "group_discom_substation") {
      if (selectedUnit === "MWh") return "demand_mwh";
      if (selectedUnit === "KWh") return "demand_kwh";
      if (selectedUnit === "KW") return "demand_kw";
      return "demand_mw";
    }
    return unitToKey[selectedUnit] || "demand_mwh";
  }, [selectedGroupBy.rowKey, selectedUnit]);

  const convertForecastMwToUnit = useCallback(
    (valueMw: number) => {
      if (selectedUnit === "KW") return valueMw * 1000;
      if (selectedUnit === "KWh") return (valueMw * 1000) / 2;
      if (selectedUnit === "MWh") return valueMw / 2;
      return valueMw;
    },
    [selectedUnit],
  );

  const forecastBySeriesXKey = useMemo(() => {
    const grouped = new Map<string, number[]>();
    for (const row of forecastRows) {
      const xKey = getXKeyForInterval(row);
      const key = `${row.substationId}__${xKey}`;
      const values = grouped.get(key) || [];
      values.push(convertForecastMwToUnit(row.forecast_mw));
      grouped.set(key, values);
    }
    const averaged = new Map<string, number>();
    grouped.forEach((values, key) =>
      averaged.set(key, values.reduce((sum, v) => sum + v, 0) / values.length),
    );
    return averaged;
  }, [forecastRows, getXKeyForInterval, convertForecastMwToUnit]);

  const appendForecastToRows = useCallback(
    (rows: any[]) => {
      if (!selectedVariables.includes("Forecast")) return rows;
      const enriched = rows.map((row) => {
        const xKey = getXKeyForInterval(row);
        const forecastCols = forecastSeries.reduce<
          Record<string, number | null>
        >((acc, series) => {
          acc[`forecast_${series.value}`] =
            forecastBySeriesXKey.get(`${series.value}__${xKey}`) ?? null;
          return acc;
        }, {});
        return { ...row, ...forecastCols };
      });

      // Collect xKeys already covered by actual rows
      const coveredXKeys = new Set(
        enriched.map((r: any) => getXKeyForInterval(r)),
      );

      // Build stub rows for forecast points that fall outside the actual date range
      const extraByXKey = new Map<string, any>();
      for (const fr of forecastRows) {
        const xKey = getXKeyForInterval(fr);
        if (coveredXKeys.has(xKey)) continue;
        if (!extraByXKey.has(xKey)) {
          extraByXKey.set(xKey, {
            date: fr.date,
            block: fr.block,
            month: fr.month,
          });
        }
        const row = extraByXKey.get(xKey);
        const colKey = `forecast_${fr.substationId}`;
        const val = convertForecastMwToUnit(fr.forecast_mw);
        row[colKey] = row[colKey] == null ? val : (row[colKey] + val) / 2;
      }

      if (extraByXKey.size === 0) return enriched;

      const extras = Array.from(extraByXKey.values()).sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (a.block ?? 0) - (b.block ?? 0);
      });
      return [...enriched, ...extras];
    },
    [
      selectedVariables,
      getXKeyForInterval,
      forecastSeries,
      forecastBySeriesXKey,
      forecastRows,
      convertForecastMwToUnit,
    ],
  );

  const enrichRowsWithWeather = useCallback(
    (rows: any[], getXKey: (row: any) => string) => {
      if (
        !selectedVariables.includes("Weather") ||
        weatherData.length === 0 ||
        weatherSeries.length === 0
      )
        return rows;

      const weatherBySeriesXKey = new Map<string, number[]>();
      for (const row of weatherData) {
        const rawValue = (row as any)[selectedWeatherParam];
        if (rawValue == null || isNaN(parseFloat(rawValue))) continue;
        const xKey = getXKey({
          ...row,
          block: row.block ?? row.time_block,
          month: row.month ?? (row.date ? getMonthStr(row.date) : ""),
        });
        const key = `${row.seriesId}__${xKey}`;
        const values = weatherBySeriesXKey.get(key) || [];
        values.push(parseFloat(rawValue));
        weatherBySeriesXKey.set(key, values);
      }

      return rows.map((row) => {
        const xKey = getXKey(row);
        const weatherCols: Record<string, number | null> = {};
        for (const series of weatherSeries) {
          const values = weatherBySeriesXKey.get(`${series.value}__${xKey}`);
          weatherCols[`weather_${selectedWeatherParam}_${series.value}`] =
            values && values.length > 0
              ? values.reduce((a, b) => a + b, 0) / values.length
              : null;
        }
        return { ...row, ...weatherCols };
      });
    },
    [selectedVariables, weatherData, weatherSeries, selectedWeatherParam],
  );

  const genDataKey = useMemo<keyof JvvnlGenerationRow>(() => {
    if ((GEN_UNITS as readonly string[]).includes(selectedUnit))
      return unitDataKey(selectedUnit);
    return "generation_mw";
  }, [selectedUnit]);

  const genByPlantXKey = useMemo(() => {
    const out = new Map<string, number[]>();
    if (selectedGroupBy.rowKey !== "group_generation") return out;
    const filtered = generationData.filter((item) => {
      if (selectedPlantIds.length > 0)
        return selectedPlantIds.includes(item.plant);
      if (selectedPlantTypes.length > 0)
        return selectedPlantTypes.includes(item.plant_type);
      return true;
    });
    for (const r of filtered) {
      const xKey = getXKeyForInterval({ ...r, month: getMonthStr(r.date) });
      const plantName = (r as any).plant_name || `Plant ${r.plant}`;
      const key = `${plantName}__${xKey}`;
      const arr = out.get(key) || [];
      const v = (r as any)[genDataKey];
      if (v !== null && v !== undefined) arr.push(v as number);
      out.set(key, arr);
    }
    return out;
  }, [
    generationData,
    selectedGroupBy.rowKey,
    selectedPlantIds,
    selectedPlantTypes,
    getXKeyForInterval,
    genDataKey,
  ]);

  const generationPlantNames = useMemo(() => {
    const names = new Set<string>();
    for (const key of genByPlantXKey.keys()) {
      const plantName = key.split("__")[0];
      names.add(plantName);
    }
    return Array.from(names);
  }, [genByPlantXKey]);

  const appendGenerationToRows = useCallback(
    (rows: any[]) => {
      if (selectedGroupBy.rowKey !== "group_generation") return rows;
      return rows.map((row) => {
        const xKey = getXKeyForInterval(row);
        const cols: Record<string, number | null> = {};
        for (const plantName of generationPlantNames) {
          const arr = genByPlantXKey.get(`${plantName}__${xKey}`);
          cols[`gen_${plantName}`] =
            arr && arr.length > 0
              ? arr.reduce((a, b) => a + b, 0) / arr.length
              : null;
        }
        return { ...row, ...cols };
      });
    },
    [
      selectedGroupBy.rowKey,
      getXKeyForInterval,
      generationPlantNames,
      genByPlantXKey,
    ],
  );

  const legends = useMemo(() => {
    const out: any[] = [];
    if (
      selectedVariables.includes("Actual") &&
      selectedGroupBy.rowKey !== "group_generation"
    ) {
      chartKeys.forEach((key, idx) => {
        out.push({
          dataKey: `${key}_${activeDataKey}`,
          name: `${normalizeDisplayLabelWithoutUndefined(key)}`,
          stroke: ExchangeColors[idx % ExchangeColors.length],
          type: selectedChartType.value as ChartType,
          stackId:
            selectedChartType.stackId !== undefined ? "Actual" : undefined,
          dashFlagKey:
            selectedGroupBy.rowKey === "group_feeder"
              ? `${key}_modelled`
              : undefined,
        });
      });
    }
    if (selectedVariables.includes("Weather")) {
      weatherSeries.forEach((series, index) => {
        out.push({
          dataKey: `weather_${selectedWeatherParam}_${series.value}`,
          name: `${selectedWeatherMetricLabel} - ${series.label}`,
          unit: selectedWeatherParamMeta.unit || "",
          stroke: lightenColor(
            ExchangeColors[(index + 4) % ExchangeColors.length],
            10,
          ),
          type: ChartType.Line as ChartType,
          yAxisId: "right" as const,
          strokeDasharray: "4 4",
        });
      });
    }
    if (selectedGroupBy.rowKey === "group_generation") {
      const genOnly = true;
      const genSeries = genOnly ? chartKeys : generationPlantNames;
      genSeries.forEach((plantName: string, index: number) => {
        out.push({
          dataKey: genOnly ? `${plantName}_${genDataKey}` : `gen_${plantName}`,
          name: `${normalizeDisplayLabelWithoutUndefined(plantName)} (Generation)`,
          stroke: lightenColor(
            ExchangeColors[(index + 2) % ExchangeColors.length],
            5,
          ),
          type: genOnly
            ? (selectedChartType.value as ChartType)
            : (ChartType.Line as ChartType),
          stackId:
            genOnly && selectedChartType.stackId !== undefined
              ? "generation"
              : undefined,
          strokeDasharray: genOnly ? undefined : "2 2",
          dashFlagKey: genOnly ? `${plantName}_modelled` : undefined,
        });
      });
    }
    if (selectedVariables.includes("Forecast")) {
      forecastSeries.forEach((substation, index) => {
        out.push({
          dataKey: `forecast_${substation.value}`,
          name: `${substation.label} (Forecast)`,
          stroke: lightenColor(
            ExchangeColors[(index + 7) % ExchangeColors.length],
            5,
          ),
          type: ChartType.Line as ChartType,
          strokeDasharray: "6 3",
        });
      });
    }
    return out;
  }, [
    selectedVariables,
    selectedGroupBy.rowKey,
    selectedWeatherParam,
    selectedWeatherParamMeta,
    chartKeys,
    selectedChartType,
    activeDataKey,
    forecastSeries,
    weatherSeries,
    selectedWeatherMetricLabel,
    generationPlantNames,
    genDataKey,
  ]);

  // Main Chart Data Generation
  useEffect(() => {
    let processedRows: any[] = [];
    let aggregationKeys: string[] = [];
    let averageKeys: string[] = [];

    // Forecast-only fallback: pivot forecastRows when actual data absent.
    const renderForecastOnlyOrEmpty = (): boolean => {
      if (
        !selectedVariables.includes("Forecast") ||
        forecastRows.length === 0
      ) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return true;
      }
      const byXKey = new Map<string, any>();
      for (const fr of forecastRows) {
        const xKey = getXKeyForInterval(fr);
        if (!byXKey.has(xKey)) {
          byXKey.set(xKey, {
            date: fr.date,
            block: fr.block,
            month: fr.month,
          });
        }
        const colKey = `forecast_${fr.substationId}`;
        const row = byXKey.get(xKey);
        const existing = row[colKey];
        const val = convertForecastMwToUnit(fr.forecast_mw);
        row[colKey] = existing == null ? val : (existing + val) / 2;
      }
      const builtRows = Array.from(byXKey.values()).sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (a.block ?? 0) - (b.block ?? 0);
      });
      setChartData(builtRows);
      setChartKeys([]);
      setTableData([]);
      return true;
    };

    const generationOnly = selectedGroupBy.rowKey === "group_generation";

    if (generationOnly) {
      const showActual = selectedVariables.includes("Actual");
      const matchesGenFilters = (r: JvvnlGenerationRow) => {
        if (selectedBlockSet && !selectedBlockSet.has(r.block)) return false;
        if (selectedPlantIds.length > 0)
          return selectedPlantIds.includes(r.plant);
        if (selectedPlantTypes.length > 0)
          return selectedPlantTypes.includes(r.plant_type);
        return true;
      };
      const hasActual = generationData.some(matchesGenFilters);
      // Forecast-only path: actual not requested, OR actual requested but absent for date.
      if (!showActual || !hasActual) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const filteredGen = generationData.filter(matchesGenFilters);
      if (filteredGen.length === 0) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return;
      }
      const genRows = filteredGen.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative"
            ? "Total"
            : (r as any).plant_name || `Plant ${r.plant}`,
      }));
      const genAggKeys = [
        "generation_kw",
        "generation_kwh",
        "generation_mw",
        "generation_mwh",
        "generation_lu",
      ];
      const genFmt = FormatChartData({
        keys: chartGroupingKeys,
        tableKeys: ["date", "block", "month"],
        groupBy: ["series_group"],
        volumeKey: "",
        aggregationKeys: genAggKeys,
        averageKeys: genAggKeys,
        averageDuplicationKeys: ["date", "block"],
        priceKey: "",
        data: genRows,
      });
      const genBase = genFmt.data || [];

      // Modelled-flag aggregation: AND of is_generation_modified across all
      // raw rows in each (xKey, series_group) bucket. Used to dash chart line
      // segments at modelled points.
      const genModelledMap = new Map<string, boolean>();
      for (const r of genRows) {
        const xKey = getXKeyForInterval(r);
        const sg = (r as any).series_group;
        const key = `${xKey}__${sg}`;
        const pointModelled = !!(r as any).is_generation_modified;
        const prev = genModelledMap.get(key);
        genModelledMap.set(
          key,
          prev === undefined ? pointModelled : prev && pointModelled,
        );
      }
      if (genModelledMap.size > 0) {
        for (const row of genBase) {
          const xKey = getXKeyForInterval(row);
          for (const k of genFmt.keys) {
            const flag = genModelledMap.get(`${xKey}__${k}`);
            if (flag) (row as any)[`${k}_modelled`] = true;
          }
        }
      }

      // Enrich weather after appending forecast stubs so future-date rows get weather too.
      const genWithForecast = appendForecastToRows(genBase);
      const enrichedGen = enrichRowsWithWeather(genWithForecast, (row: any) =>
        getXKeyForInterval(row),
      );

      setChartData(enrichedGen);
      setChartKeys(genFmt.keys);
      setTableData(genFmt.tableData || []);
      return;
    }

    if (selectedGroupBy.rowKey === "group_net_consumption") {
      const showActual = selectedVariables.includes("Actual");
      if (!showActual) {
        renderForecastOnlyOrEmpty();
        return;
      }
      if (netLoadSummary.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? netLoadSummary.filter((r) => selectedBlockSet.has(r.block))
        : netLoadSummary;
      const baseLabel =
        selectedFunction === "Cumulative" ? "Total" : netSeriesLabel;
      processedRows = rows.flatMap((r) => {
        const common = {
          date: r.date,
          slot: r.slot,
          block: r.block,
          month: getMonthStr(r.date),
        };
        const all = [
          {
            ...common,
            net_mw: r.net_load_mw,
            series_group: `${baseLabel} (Net)`,
            _var: "Net Load",
          },
          {
            ...common,
            net_mw: r.demand_mw,
            series_group: `${baseLabel} (Demand)`,
            _var: "Demand",
          },
          {
            ...common,
            net_mw: r.generation_mw,
            series_group: `${baseLabel} (Generation)`,
            _var: "Generation",
          },
        ];
        return netVariable ? all.filter((x) => x._var === netVariable) : all;
      });
      aggregationKeys = ["net_mw"];
      averageKeys = ["net_mw"];
    } else if (selectedGroupBy.rowKey === "group_discom_substation") {
      if (discomSubLoadData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? discomSubLoadData.filter((r) => selectedBlockSet.has(r.block))
        : discomSubLoadData;
      processedRows = rows.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative" ? "Total" : r.series_group,
      }));
      aggregationKeys = ["demand_kw", "demand_kwh", "demand_mw", "demand_mwh"];
      averageKeys = ["demand_kw", "demand_mw"];
    } else if (selectedGroupBy.rowKey === "group_discom") {
      if (discomData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? discomData.filter((r) => selectedBlockSet.has(r.block))
        : discomData;
      processedRows = rows.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group: selectedFunction === "Cumulative" ? "Total" : "KOTPUTLI",
      }));
      aggregationKeys = ["demand_mw", "demand_mwh"];
      averageKeys = ["demand_mw"];
    } else if (selectedGroupBy.rowKey === "group_substation") {
      if (gssLoadData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? gssLoadData.filter((r) => selectedBlockSet.has(r.block))
        : gssLoadData;
      processedRows = rows.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative" ? "Total" : r.series_group,
      }));
      aggregationKeys = ["demand_mw", "demand_mwh"];
      averageKeys = ["demand_mw"];
    } else {
      if (rawData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? rawData.filter((r) => selectedBlockSet.has(r.block))
        : rawData;
      const filtered = rows.filter(
        (item) =>
          effectiveFeederIds.length === 0 ||
          effectiveFeederIds.includes(item.feeder),
      );
      processedRows = filtered.map((item) => {
        const groupValue = item.feeder_name || `Feeder ${item.feeder}`;
        return {
          ...item,
          demand_mw: item.demand_kw / 1000,
          demand_mwh: item.demand_kwh / 1000,
          month: getMonthStr(item.date),
          series_group:
            selectedFunction === "Cumulative" ? "Total" : groupValue,
        };
      });
      aggregationKeys = [
        "demand_kw",
        "demand_kwh",
        "demand_kva",
        "demand_kvah",
        "demand_mw",
        "demand_mwh",
      ];
      averageKeys = ["demand_kw", "demand_kva", "demand_mw"];
    }

    const cData = FormatChartData({
      keys: chartGroupingKeys,
      tableKeys: ["date", "block", "month"],
      groupBy: ["series_group"],
      volumeKey: "",
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: processedRows,
    });

    // Modelled-flag aggregation: AND of is_demand_kwh_modelled &&
    // is_demand_kavh_modelled across all raw rows in each (xKey, series_group)
    // bucket. Used to dash chart line segments at modelled points.
    const modelledIsFeeder = selectedGroupBy.rowKey === "group_feeder";
    const modelledMap = new Map<string, boolean>();
    if (modelledIsFeeder) {
      for (const r of processedRows) {
        const xKey = getXKeyForInterval(r);
        const sg = (r as any).series_group;
        const key = `${xKey}__${sg}`;
        const pointModelled =
          !!(r as any).is_demand_kwh_modelled &&
          !!(r as any).is_demand_kavh_modelled;
        const prev = modelledMap.get(key);
        modelledMap.set(
          key,
          prev === undefined ? pointModelled : prev && pointModelled,
        );
      }
    }

    const withModelled = cData.data || [];

    if (modelledIsFeeder && modelledMap.size > 0) {
      for (const row of withModelled) {
        const xKey = getXKeyForInterval(row);
        for (const k of cData.keys) {
          const flag = modelledMap.get(`${xKey}__${k}`);
          if (flag) (row as any)[`${k}_modelled`] = true;
        }
      }
    }

    // Enrich weather AFTER appending forecast stubs so that forecast-only rows
    // (e.g. future dates with no actual data) also get weather data.
    const withForecast = appendForecastToRows(withModelled);
    const enriched = enrichRowsWithWeather(withForecast, (row: any) =>
      getXKeyForInterval(row),
    );

    setChartData(appendGenerationToRows(enriched));
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    rawData,
    gssLoadData,
    discomData,
    selectedGroupBy.rowKey,
    selectedBlock,
    selectedFunction,
    selectedInterval,
    chartGroupingKeys,
    effectiveFeederIds,
    enrichRowsWithWeather,
    getXKeyForInterval,
    appendForecastToRows,
    appendGenerationToRows,
    generationData,
    selectedPlantIds,
    selectedPlantTypes,
    selectedBlockSet,
    selectedVariables,
    feeders,
    discomSubLoadData,
    selectedDiscomSubstationIds,
    selectedDiscomSubstations,
    netLoadSummary,
    netSeriesLabel,
    netVariable,
    forecastRows,
    convertForecastMwToUnit,
  ]);

  // ============================================================================
  // 6. RENDER
  // ============================================================================
  const xDataKey =
    selectedInterval.key === "time" ? "block" : selectedInterval.key;
  const secondDataKey = () => (selectedInterval.isTime ? "date" : undefined);

  const blockDurationMinutes = useMemo(() => {
    const sources: { block?: number }[] =
      selectedGroupBy.rowKey === "group_net_consumption"
        ? netLoadSummary
        : selectedGroupBy.rowKey === "group_discom_substation"
          ? discomSubLoadData
          : selectedGroupBy.rowKey === "group_discom"
            ? discomData
            : selectedGroupBy.rowKey === "group_substation"
              ? gssLoadData
              : selectedGroupBy.rowKey === "group_generation"
                ? generationData
                : rawData;
    let maxBlock = 0;
    for (const r of sources) {
      if (r.block != null && r.block > maxBlock) maxBlock = r.block;
    }
    return maxBlock > 48 ? 15 : 30;
  }, [
    selectedGroupBy.rowKey,
    netLoadSummary,
    discomSubLoadData,
    discomData,
    gssLoadData,
    generationData,
    rawData,
  ]);
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const showGenerationSearch = selectedGroupBy.rowKey === "group_generation";
  const showNetConsumptionSearch =
    selectedGroupBy.rowKey === "group_net_consumption";
  const showFeederSearch = selectedGroupBy.rowKey === "group_feeder";
  const showDiscomSubstationSearch =
    selectedGroupBy.rowKey === "group_discom_substation";
  const showSubstationSearch =
    selectedGroupBy.rowKey === "group_substation" ||
    (selectedVariables.includes("Forecast") &&
      selectedGroupBy.rowKey !== "group_discom" &&
      selectedGroupBy.rowKey !== "group_discom_substation" &&
      selectedGroupBy.rowKey !== "group_feeder" &&
      selectedGroupBy.rowKey !== "group_generation" &&
      selectedGroupBy.rowKey !== "group_net_consumption");

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: selectedInterval.isTime
      ? ["block", selectedInterval.values[1]]
      : [selectedInterval.values[0]],
    extraLabels: selectedInterval.values.map((v) =>
      v === "date" ? "Date" : v,
    ),
    filename: AddDatesWithFileName({
      fileName: "JVVNL Overview",
      dates: currentRangeDate,
    }),
  };

  const feederFull = feederMetrics?.metrics?.full;
  const feederPeakLoadKw =
    feederFull?.peak_mw != null ? Number(feederFull.peak_mw) : null;
  const feederAvgLoadKw =
    feederFull?.avg_mw != null ? Number(feederFull.avg_mw) : null;
  const feederPeakTemp =
    feederFull?.peak_weather?.temp != null
      ? Number(feederFull.peak_weather.temp)
      : null;
  const feederAvgTemp =
    feederFull?.avg_weather?.temp != null
      ? Number(feederFull.avg_weather.temp)
      : null;

  const groupFull = groupMetrics?.metrics?.full;
  const groupPeakLoad =
    groupFull?.peak_mw != null ? Number(groupFull.peak_mw) : null;
  const groupAvgLoad =
    groupFull?.avg_mw != null ? Number(groupFull.avg_mw) : null;
  const groupPeakTemp =
    groupFull?.peak_weather?.temp != null
      ? Number(groupFull.peak_weather.temp)
      : null;
  const groupAvgTemp =
    groupFull?.avg_weather?.temp != null
      ? Number(groupFull.avg_weather.temp)
      : null;
  const groupLoadUnit =
    selectedGroupBy.rowKey === "group_discom_substation" ? "kW" : "MW";
  const groupLabelPrefix =
    selectedGroupBy.rowKey === "group_substation"
      ? "Transco"
      : selectedGroupBy.rowKey === "group_discom_substation"
        ? "Discom Substation"
        : "Circle";

  // Embedded mode: local view tab (Chart/Summary/Table)
  const [embeddedView, setEmbeddedView] = useState<
    "Chart" | "Summary" | "Table"
  >("Chart");

  // Embedded mode: seed plant selection from caller-provided entity
  const embeddedGenSeededRef = useRef(false);
  useEffect(() => {
    if (!embedded || embedded.level !== "plant") return;
    if (embeddedGenSeededRef.current) return;
    if (allPlants.length === 0) return;
    const plant = allPlants.find((p) => p.id === embedded.id);
    if (!plant) return;
    setSelectedPlantTypeOptions([
      { value: plant.plant_type, label: plant.plant_type },
    ]);
    setSelectedPlantOptions([{ value: plant.id, label: plant.name }]);
    didInitPlantDefaultsRef.current = true;
    embeddedGenSeededRef.current = true;
  }, [embedded, allPlants]);

  // Embedded mode: seed transco/discom from caller-provided entity
  const embeddedSeededRef = useRef(false);
  useEffect(() => {
    if (!embedded) return;
    if (embeddedSeededRef.current) return;
    if (embedded.level === "feeder") {
      const embeddedFeederId = Number(embedded.id);
      if (Number.isNaN(embeddedFeederId)) return;
      if (feeders.length === 0) {
        if (selectedFeederOptions.length === 0) {
          setSelectedFeederOptions([
            { value: embeddedFeederId, label: embedded.label },
          ]);
        }
        return;
      }
      const raw = feeders.find((f) => f.id === embeddedFeederId);
      if (raw) {
        setSelectedFeederOptions([{ value: raw.id, label: raw.name }]);
        if (raw.discom_substation_name) {
          setSelectedFeederSubstations([
            {
              value: raw.discom_substation_name,
              label: raw.discom_substation_name,
            },
          ]);
        }
      } else {
        setSelectedFeederOptions([
          { value: embeddedFeederId, label: embedded.label },
        ]);
      }
      embeddedSeededRef.current = true;
      return;
    }
    if (embedded.level !== "plant" && discomSubstations.length === 0) return;
    if (embedded.level === "circle") {
      setNetCircle({ value: String(embedded.id), label: embedded.label });
      setNetTransco(null);
      setNetDiscom(null);
      embeddedSeededRef.current = true;
    } else if (embedded.level === "transco") {
      setNetTransco({ value: Number(embedded.id), label: embedded.label });
      setNetDiscom(null);
      embeddedSeededRef.current = true;
    } else if (embedded.level === "discom_sub") {
      const raw = discomSubstations.find(
        (d) => d.discom_substation_id === Number(embedded.id),
      );
      setNetDiscom({ value: Number(embedded.id), label: embedded.label });
      if (raw?.transco_substation_id) {
        setNetTransco({
          value: raw.transco_substation_id,
          label:
            raw.transco_substation_name ||
            `Transco ${raw.transco_substation_id}`,
        });
      }
      embeddedSeededRef.current = true;
    }
  }, [embedded, discomSubstations, feeders, selectedFeederOptions.length]);

  useEffect(() => {
    if (embedded?.level !== "feeder") return;
    if (selectedDiscomSubstations.length > 0) return;
    if (selectedTranscoForDiscom.length > 0) return;
    if (selectedFeederOptions.length === 0) return;
    if (feeders.length === 0 || discomSubstations.length === 0) return;
    const feederId = selectedFeederOptions[0]?.value;
    const feeder = feeders.find((f) => f.id === feederId);
    if (!feeder || feeder.discom_substation_id == null) return;
    const discom = discomSubstations.find(
      (d) => d.discom_substation_id === feeder.discom_substation_id,
    );
    if (!discom) return;
    setSelectedDiscomSubstations([
      {
        value: discom.discom_substation_id,
        label: discom.discom_substation_name,
      },
    ]);
    if (discom.transco_substation_id) {
      setSelectedTranscoForDiscom([
        {
          value: discom.transco_substation_id,
          label:
            discom.transco_substation_name ||
            `Transco ${discom.transco_substation_id}`,
        },
      ]);
    }
  }, [
    embedded,
    selectedDiscomSubstations.length,
    selectedTranscoForDiscom.length,
    selectedFeederOptions,
    feeders,
    discomSubstations,
  ]);

  if (embedded) {
    return (
      <div className="flex flex-col gap-3 p-3 relative">
        <button
          onClick={() =>
            [downloadChartTableCSV, downloadSummaryCSV, downloadChartTableCSV][
              ["Chart", "Summary", "Table"].indexOf(embeddedView)
            ]?.(tablePar)
          }
          className="group absolute bottom-3 right-3 z-50 flex items-center gap-1.5 h-9 w-9 hover:w-[140px] overflow-hidden bg-[#F1935C] hover:bg-[#E07A3D] text-white rounded-full shadow-lg transition-all duration-300 ease-out px-2.5"
          title="Download Data"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span className="text-[12px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            Download Data
          </span>
        </button>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : chartData.length === 0 ? (
            <div className="relative min-h-[300px]">
              <EmptyState />
            </div>
          ) : (
            <div className="flex flex-col gap-y-2 justify-between border-gray-200 bg-white">
              <div className="gas-heading gap-x-10 items-center">
                <h2 className="chartHeading w-60">
                  {embedded.level === "plant"
                    ? "Generation"
                    : embedded.level === "feeder"
                      ? "Feeder Demand"
                      : "Net Consumption"}
                </h2>
                {embedded.level !== "plant" && embedded.level !== "feeder" && (
                  <div className="flex gap-x-3 items-center flex-1">
                    {(embedded.level === "circle" ||
                      embedded.level === "transco" ||
                      embedded.level === "discom_sub") && (
                      <SearchBox
                        isMany
                        options={[NET_CONSUMPTION_CIRCLE]}
                        placeholder="Search Circle"
                        selectedOptions={netCircle ? [netCircle] : []}
                        onChange={(v: any) => {
                          const next = Array.isArray(v)
                            ? (v[0] ?? null)
                            : (v ?? null);
                          setNetCircle(next);
                          setNetTransco(null);
                          setNetDiscom(null);
                        }}
                      />
                    )}
                    {(embedded.level === "transco" ||
                      embedded.level === "discom_sub") && (
                      <SearchBox
                        isMany
                        sortAlphabetically
                        options={netTranscoOptions}
                        placeholder="Search Transco Substation"
                        selectedOptions={netTransco ? [netTransco] : []}
                        onChange={(v: any) => {
                          const next = Array.isArray(v)
                            ? (v[0] ?? null)
                            : (v ?? null);
                          setNetTransco(next);
                          setNetDiscom(null);
                        }}
                      />
                    )}
                    {embedded.level === "discom_sub" && (
                      <SearchBox
                        isMany
                        sortAlphabetically
                        options={netDiscomOptions}
                        placeholder="Search Discom Substation"
                        selectedOptions={netDiscom ? [netDiscom] : []}
                        onChange={(v: any) => {
                          const next = Array.isArray(v)
                            ? (v[0] ?? null)
                            : (v ?? null);
                          setNetDiscom(next);
                          if (next) {
                            const raw = discomSubstations.find(
                              (d) => d.discom_substation_id === next.value,
                            );
                            if (raw?.transco_substation_id) {
                              const t = netTranscoOptions.find(
                                (x) => x.value === raw.transco_substation_id,
                              );
                              if (t) setNetTransco(t);
                            }
                          }
                        }}
                      />
                    )}
                  </div>
                )}
                {embedded.level === "feeder" && (
                  <div className="flex gap-x-3 items-center flex-1">
                    <SearchBox
                      isMany
                      options={[NET_CONSUMPTION_CIRCLE]}
                      placeholder="Search Circle"
                      selectedOptions={netCircle ? [netCircle] : []}
                      onChange={(v: any) => {
                        const next = Array.isArray(v)
                          ? (v[0] ?? null)
                          : (v ?? null);
                        setNetCircle(next);
                        setSelectedTranscoForDiscom([]);
                        setSelectedDiscomSubstations([]);
                        setSelectedFeederSubstations([]);
                        setSelectedFeederOptions([]);
                      }}
                    />
                    <SearchBox
                      isMany
                      sortAlphabetically
                      options={transcoOptionsForDiscom}
                      placeholder="Search Transco Substation"
                      selectedOptions={selectedTranscoForDiscom}
                      onChange={(v: any) => {
                        const next = isolateOnAllSelected(
                          selectedTranscoForDiscom,
                          v ?? [],
                          transcoOptionsForDiscom,
                        );
                        setSelectedTranscoForDiscom(next);
                        setSelectedDiscomSubstations([]);
                        setSelectedFeederSubstations([]);
                        setSelectedFeederOptions([]);
                      }}
                    />
                    <SearchBox
                      isMany
                      sortAlphabetically
                      options={filteredDiscomSubstationOptions}
                      placeholder="Search Discom Substation"
                      selectedOptions={selectedDiscomSubstations}
                      onChange={(v: any) => {
                        const next = isolateOnAllSelected(
                          selectedDiscomSubstations,
                          v ?? [],
                          filteredDiscomSubstationOptions,
                        );
                        setSelectedDiscomSubstations(next);
                        setSelectedFeederSubstations(
                          next.map((o: any) => ({
                            value: o.label,
                            label: o.label,
                          })),
                        );
                        setSelectedFeederOptions([]);
                      }}
                    />
                    <SearchBox
                      sortAlphabetically={true}
                      options={filteredFeederOptions}
                      isMany
                      placeholder="Search Feeder"
                      selectedOptions={selectedFeederOptions}
                      onChange={(v: any) => {
                        setSelectedFeederOptions(
                          isolateOnAllSelected(
                            selectedFeederOptions,
                            v ?? [],
                            filteredFeederOptions,
                          ),
                        );
                      }}
                    />
                  </div>
                )}
                <FilterTab
                  options={DataTabs.map((item) => item.label)}
                  selected={[embeddedView]}
                  onClick={(opt) =>
                    setEmbeddedView(opt as "Chart" | "Summary" | "Table")
                  }
                  variant="pill-outline"
                />
              </div>
              {embeddedView === "Chart" && (
                <ReMixChart
                  key={`jvvnl-embedded-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}-${selectedFunction}`}
                  data={chartData}
                  showBrush
                  brushIndex={{
                    startIndex: 0,
                    endIndex: Math.max(0, chartData.length - 1),
                  }}
                  isTimeSlot={selectedInterval.isTime}
                  blockDurationMinutes={blockDurationMinutes}
                  onlyTitle
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  secondYAxisLabel={
                    selectedVariables.includes("Weather")
                      ? selectedWeatherMetricLabel
                      : undefined
                  }
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (v: any) =>
                          renderHourTick(
                            v,
                            10,
                            blockDurationMinutes === 30 ? 2 : 4,
                          )
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {embeddedView === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {embeddedView === "Table" && <ChartTable {...tablePar} />}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Intervals"
            items={Intervals.map((it) => it.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(_, i) => setSelectedInterval(Intervals[i])}
          />
          <FilterMenu
            title="Unit"
            items={
              generationOnlyMode
                ? [...JVVNL_GEN_UNITS]
                : selectedGroupBy.rowKey === "group_feeder" ||
                    selectedGroupBy.rowKey === "group_discom_substation"
                  ? [...JVVNL_DEMAND_UNITS]
                  : [...JVVNL_AGG_UNITS]
            }
            selectedItems={[selectedUnit]}
            onSelect={setSelectedUnit}
          />
          <FilterMenu
            title="Function"
            items={[...FUNCTIONS]}
            selectedItems={[selectedFunction]}
            onSelect={(_, i) => setSelectedFunction(FUNCTIONS[i])}
          />
          <FilterMenu
            title="Group By"
            sortAlphabetically={false}
            items={GROUP_BY_OPTIONS.map((i) => i.label)}
            selectedItems={[selectedGroupBy.label]}
            onSelect={(_, i) => setSelectedGroupBy(GROUP_BY_OPTIONS[i])}
          />
          <FilterMenu
            title="Variable"
            items={[...VARIABLES]}
            selectedItems={selectedVariables}
            setSelectedItems={(next: string[]) => setSelectedVariables(next)}
          />
          <FilterMenu
            title="Weather Metric"
            items={WEATHER_PARAMS.map((w) => ({
              value: w.key,
              label: w.label,
            }))}
            selectedItems={[selectedWeatherParam]}
            onSelect={(i) => setSelectedWeatherParams([i])}
            disabled={!selectedVariables.includes("Weather")}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((it) => it.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, i) => setSelectedChartType(R_CHART_TYPES[i])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          {selectedGroupBy.rowKey === "group_net_consumption" ? (
            <>
              <MetricChip
                items={[
                  {
                    label: "Peak Load",
                    value:
                      netMetrics?.peak_load_kw != null
                        ? Math.round((netMetrics.peak_load_kw / 1000) * 100) /
                          100
                        : undefined,
                    unit: "MW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Load",
                    value:
                      netMetrics?.avg_load_kw != null
                        ? Math.round((netMetrics.avg_load_kw / 1000) * 100) /
                          100
                        : undefined,
                    unit: "MW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Peak Temp",
                    value:
                      netMetrics?.peak_temp_c != null
                        ? Math.round(netMetrics.peak_temp_c * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Temp",
                    value:
                      netMetrics?.avg_temp_c != null
                        ? Math.round(netMetrics.avg_temp_c * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          ) : selectedGroupBy.rowKey === "group_feeder" ? (
            <>
              <MetricChip
                items={[
                  {
                    label: "Peak Load",
                    value:
                      feederPeakLoadKw != null
                        ? Math.round(feederPeakLoadKw * 100) / 100
                        : undefined,
                    unit: "kW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Load",
                    value:
                      feederAvgLoadKw != null
                        ? Math.round(feederAvgLoadKw * 100) / 100
                        : undefined,
                    unit: "kW",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Peak Temp",
                    value:
                      feederPeakTemp != null
                        ? Math.round(feederPeakTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: "Average Temp",
                    value:
                      feederAvgTemp != null
                        ? Math.round(feederAvgTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          ) : (
            <>
              <MetricChip
                items={[
                  {
                    label: `Peak ${groupLabelPrefix} Load`,
                    value:
                      groupPeakLoad != null
                        ? Math.round(groupPeakLoad * 100) / 100
                        : undefined,
                    unit: groupLoadUnit,
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: `Average ${groupLabelPrefix} Load`,
                    value:
                      groupAvgLoad != null
                        ? Math.round(groupAvgLoad * 100) / 100
                        : undefined,
                    unit: groupLoadUnit,
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: `Peak ${groupLabelPrefix} Temp`,
                    value:
                      groupPeakTemp != null
                        ? Math.round(groupPeakTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
              <MetricChip
                items={[
                  {
                    label: `Average ${groupLabelPrefix} Temp`,
                    value:
                      groupAvgTemp != null
                        ? Math.round(groupAvgTemp * 100) / 100
                        : undefined,
                    unit: "℃",
                  },
                ]}
              />
            </>
          )}
        </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="flex flex-col gap-y-2 justify-between border-gray-200 bg-white">
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">{selectedGroupBy.label}</h2>
                  <div className="flex gap-x-6 justify-end items-center">
                    {showFeederSearch && (
                      <>
                        <SearchBox
                          isMany
                          sortAlphabetically={true}
                          options={feederSubstationOptions}
                          placeholder="Search Discom Substation"
                          selectedOptions={selectedFeederSubstations}
                          onChange={(v: any) => {
                            const next = v ?? [];
                            setSelectedFeederSubstations(
                              isolateOnAllSelected(
                                selectedFeederSubstations,
                                next,
                                feederSubstationOptions,
                              ),
                            );
                          }}
                        />
                        <SearchBox
                          sortAlphabetically={true}
                          options={filteredFeederOptions}
                          isMany
                          placeholder="Search Feeder"
                          selectedOptions={selectedFeederOptions}
                          onChange={(v: any) => {
                            const next = v ?? [];
                            setSelectedFeederOptions(
                              isolateOnAllSelected(
                                selectedFeederOptions,
                                next,
                                filteredFeederOptions,
                              ),
                            );
                          }}
                        />
                      </>
                    )}
                    {showSubstationSearch && (
                      <SearchBox
                        sortAlphabetically={true}
                        options={substationOptions}
                        isMany
                        placeholder="Search Substation"
                        selectedOptions={selectedSubstationOptions}
                        onChange={(v: any) => {
                          const next = v ?? [];
                          setSelectedSubstationOptions(
                            isolateOnAllSelected(
                              selectedSubstationOptions,
                              next,
                              substationOptions,
                            ),
                          );
                        }}
                      />
                    )}
                    {showDiscomSubstationSearch && (
                      <>
                        <SearchBox
                          isMany
                          sortAlphabetically={true}
                          options={transcoOptionsForDiscom}
                          placeholder="Search Transco Substation"
                          selectedOptions={selectedTranscoForDiscom}
                          onChange={(v: any) => {
                            const next = v ?? [];
                            setSelectedTranscoForDiscom(
                              isolateOnAllSelected(
                                selectedTranscoForDiscom,
                                next,
                                transcoOptionsForDiscom,
                              ),
                            );
                          }}
                        />
                        <SearchBox
                          isMany
                          sortAlphabetically={true}
                          options={filteredDiscomSubstationOptions}
                          placeholder="Search Discom Substation"
                          selectedOptions={selectedDiscomSubstations}
                          onChange={(v: any) => {
                            const next = v ?? [];
                            setSelectedDiscomSubstations(
                              isolateOnAllSelected(
                                selectedDiscomSubstations,
                                next,
                                filteredDiscomSubstationOptions,
                              ),
                            );
                          }}
                        />
                      </>
                    )}
                    {showNetConsumptionSearch && (
                      <>
                        <SearchBox
                          isMany={true}
                          options={[NET_CONSUMPTION_CIRCLE]}
                          placeholder="Search Circle"
                          selectedOptions={netCircle ? [netCircle] : []}
                          onChange={(v: any) => {
                            const next = Array.isArray(v)
                              ? (v[0] ?? null)
                              : (v ?? null);
                            setNetCircle(next);
                            setNetTransco(null);
                            setNetDiscom(null);
                          }}
                        />
                        <SearchBox
                          isMany={true}
                          sortAlphabetically={true}
                          options={netTranscoOptions}
                          placeholder="Search Transco Substation"
                          selectedOptions={netTransco ? [netTransco] : []}
                          onChange={(v: any) => {
                            const prev = netTransco ? [netTransco] : [];
                            const raw: any[] = Array.isArray(v)
                              ? v
                              : v
                                ? [v]
                                : [];
                            const resolved = isolateOnAllSelected(
                              prev,
                              raw,
                              netTranscoOptions,
                            );
                            const added = resolved.find(
                              (o) => !prev.some((p) => p.value === o.value),
                            );
                            const next =
                              added ?? resolved[resolved.length - 1] ?? null;
                            setNetTransco(next);
                            setNetDiscom(null);
                          }}
                        />
                        <SearchBox
                          isMany={true}
                          sortAlphabetically={true}
                          options={netDiscomOptions}
                          placeholder="Search Discom Substation"
                          selectedOptions={netDiscom ? [netDiscom] : []}
                          onChange={(v: any) => {
                            const prev = netDiscom ? [netDiscom] : [];
                            const raw: any[] = Array.isArray(v)
                              ? v
                              : v
                                ? [v]
                                : [];
                            const resolved = isolateOnAllSelected(
                              prev,
                              raw,
                              netDiscomOptions,
                            );
                            const added = resolved.find(
                              (o) => !prev.some((p) => p.value === o.value),
                            );
                            const next =
                              added ?? resolved[resolved.length - 1] ?? null;
                            setNetDiscom(next);
                            // backward map: auto-fill transco
                            if (next) {
                              const discomRaw = discomSubstations.find(
                                (d) => d.discom_substation_id === next.value,
                              );
                              if (discomRaw?.transco_substation_id) {
                                const t = netTranscoOptions.find(
                                  (x) =>
                                    x.value === discomRaw.transco_substation_id,
                                );
                                if (t) setNetTransco(t);
                              }
                            }
                          }}
                        />
                      </>
                    )}
                    {showGenerationSearch && (
                      <>
                        <SearchBox
                          sortAlphabetically={true}
                          options={plantTypeOptions}
                          isMany={true}
                          placeholder="Search Plant Type"
                          selectedOptions={selectedPlantTypeOptions}
                          onChange={(val: any[]) => {
                            const next = val || [];
                            setSelectedPlantTypeOptions(
                              isolateOnAllSelected(
                                selectedPlantTypeOptions,
                                next,
                                plantTypeOptions,
                              ),
                            );
                          }}
                        />
                        <SearchBox
                          sortAlphabetically={true}
                          options={plantOptions}
                          isMany={true}
                          placeholder="Search Plant"
                          selectedOptions={selectedPlantOptions}
                          onChange={(v: any) => {
                            const raw: { value: number; label: string }[] =
                              v || [];
                            const selected = isolateOnAllSelected(
                              selectedPlantOptions,
                              raw,
                              plantOptions,
                            );
                            setSelectedPlantOptions(selected);
                            if (selected.length > 0) {
                              const types = Array.from(
                                new Set(
                                  selected
                                    .map(
                                      (o) =>
                                        allPlants.find((p) => p.id === o.value)
                                          ?.plant_type,
                                    )
                                    .filter(Boolean) as JvvnlPlantType[],
                                ),
                              ).map((t) => ({ value: t, label: t }));
                              setSelectedPlantTypeOptions(types);
                            }
                          }}
                        />
                      </>
                    )}
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Search Block"
                      selectedOptions={selectedBlock.map((it) => it.value)}
                      setSelectedOptions={setSelectedBlock as any}
                    />
                    <button
                      onClick={() =>
                        [
                          downloadChartTableCSV,
                          downloadSummaryCSV,
                          downloadChartTableCSV,
                        ][
                          ["Chart", "Summary", "Table"].indexOf(dataTab.label)
                        ]?.(tablePar)
                      }
                      className="downloadButton"
                    >
                      Download Data
                    </button>
                  </div>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`jvvnl-${selectedUnit}-${selectedInterval.key}-${selectedChartType.value}-${selectedFunction}`}
                  data={chartData}
                  showBrush={chartData.length > 0}
                  brushIndex={{
                    startIndex: 0,
                    endIndex: Math.max(0, chartData.length - 1),
                  }}
                  isTimeSlot={selectedInterval.isTime}
                  blockDurationMinutes={blockDurationMinutes}
                  onlyTitle
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  secondYAxisLabel={
                    selectedVariables.includes("Weather")
                      ? selectedWeatherMetricLabel
                      : undefined
                  }
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (v: any) =>
                          renderHourTick(
                            v,
                            10,
                            blockDurationMinutes === 30 ? 2 : 4,
                          )
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>Source:</p>}
              text="1. Weather : Open-Meteo&#10;2. Load : Discom&#10;3. Feeder : AMISP&#10;4. Generation : AMISP (HKRP)&#10;"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
